import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import isEmpty from "lodash-es/isEmpty";

import { Company, EntitiesSupportingEmail, EntityType } from "@mapmycustomers/shared/types/entity";
import { Person } from "@mapmycustomers/shared/types/entity";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import { fetchPeople } from "@app/store/person/actions";

import EmailEntities from "../EmailEntities";
import RecipientType from "../RecipientType";

const getEntityKey = ({ id, entity }: Company | Person): string => `${entity}_${id}`;

const useEmailEntities = (
  selectedEntities: EntitiesSupportingEmail[],
  onLoading: (loading: boolean) => void
): EmailEntities => {
  const dispatch = useDispatch();

  const [emailEntities, setEmailEntities] = useState<EmailEntities>(() => ({
    allCompanies: [],
    companies: [],
    hasCompanyPersonEntities: false,
    hasEntitiesFromDeal: false,
    people: [],
    recipientTypes: new Set(),
    someEntitiesDontHaveEmail: false,
  }));

  useEffect(() => {
    onLoading(true);

    let hasEntitiesFromDeal = false;
    let someEntitiesDontHaveEmail = false;
    let hasCompanyPersonEntities = false;

    const companies: Company[] = [];
    const people: Person[] = [];

    const allCompanies: Company[] = [];
    const processedEntities: Set<string> = new Set();

    const processEntity = (entity: Company | Person | undefined): boolean => {
      if (entity) {
        const hasEmail = !!entity.email;
        const key = getEntityKey(entity);
        if (!processedEntities.has(key)) {
          processedEntities.add(key);
          switch (entity.entity) {
            case EntityType.COMPANY:
              allCompanies.push(entity);
              if (hasEmail) {
                companies.push(entity);
              }
              break;
            case EntityType.PERSON:
              if (hasEmail) {
                people.push(entity);
              }
              break;
          }
          return true;
        }
      }
      return false;
    };

    const updateSelectedEntitiesInfo = () => {
      const recipientTypes: (RecipientType | undefined)[] = [
        companies.length ? RecipientType.Company : undefined,
        hasCompanyPersonEntities ? RecipientType.CompanyPerson : undefined,
        people.length ? RecipientType.Person : undefined,
      ];

      setEmailEntities({
        allCompanies,
        companies,
        hasEntitiesFromDeal,
        people,
        recipientTypes: new Set<RecipientType>(recipientTypes.filter(isDefined)),
        someEntitiesDontHaveEmail,
      });

      onLoading(false);
    };

    for (const entity of selectedEntities) {
      if (entity.entity === EntityType.DEAL) {
        const processedCompany = processEntity(entity.account);
        const processedPerson = processEntity(entity.contact);
        hasEntitiesFromDeal = hasEntitiesFromDeal || processedCompany || processedPerson;
      } else {
        processEntity(entity);
        someEntitiesDontHaveEmail = someEntitiesDontHaveEmail || isEmpty(entity.email);
      }
    }

    if (allCompanies.length && !hasEntitiesFromDeal) {
      // in future if we will have multiply utilities related to selected records relationships
      // it is better to move it to some separate common store for selected records
      dispatch(
        fetchPeople({
          callback: (peopleList) => {
            hasCompanyPersonEntities = !!peopleList.total;
            updateSelectedEntitiesInfo();
          },
          failureCallback: () => updateSelectedEntitiesInfo(),
          options: {
            $filters: {
              $and: [
                {
                  "accounts.id": { $in: allCompanies.map(({ id }) => id) },
                  email: {
                    $ne: null,
                  },
                },
              ],
            },
            $limit: 0,
            $order: "name",
          },
        })
      );
    } else {
      updateSelectedEntitiesInfo();
    }
  }, [dispatch, selectedEntities, onLoading]);

  return emailEntities;
};

export default useEmailEntities;
